import { useMemo } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";

import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import type { VacationRequestRowUiT } from "../domain/ui/vacationRequestRow.model";
import type { ApplyRequestsMutationResponseT } from "../domain/types/applyRequests.types";

type ApplyResultItemT = ApplyRequestsMutationResponseT["results"][number];

type ApplyResultRowT = {
  requestId: string;
  fullName: string;
  requestType: string;
  isSuccess?: boolean;
  message: string;
};

type ApplyResultDialogPropsT = {
  visible: boolean;
  rows: VacationRequestRowUiT[];
  results: ApplyResultItemT[];
  onClose: () => void;
};

const ApplyResultDialog = ({
  visible,
  rows,
  results,
  onClose,
}: ApplyResultDialogPropsT) => {
  const data: ApplyResultRowT[] = useMemo(() => {
    const rowsById = new Map(rows.map((row) => [row.id, row]));
    return (results ?? []).map((result) => {
      const row = rowsById.get(result.requestId);
      return {
        requestId: result.requestId,
        fullName: row?.fullName ?? "—",
        requestType: row?.requestType ?? "—",
        isSuccess: result.isSuccess,
        message: result.isSuccess
          ? VACATION_REQUESTS_STRINGS.APPLY_RESULT_OK_LABEL
          : result.status,
      };
    });
  }, [rows, results]);

  const okCount = useMemo(
    () => data.filter((d) => d.isSuccess).length,
    [data],
  );
  const errCount = data.length - okCount;

  const header = VACATION_REQUESTS_STRINGS.APPLY_RESULT_HEADER_TEMPLATE.replace(
    "{ok}",
    String(okCount),
  ).replace("{err}", String(errCount));

  const statusBody = (rowData: ApplyResultRowT) =>
    rowData.isSuccess ? (
      <span className="flex items-center justify-center gap-1 text-green-600">
        <CheckCircleIcon size={22} weight="fill" />
        {VACATION_REQUESTS_STRINGS.APPLY_RESULT_OK_LABEL}
      </span>
    ) : (
      <span className="flex items-center justify-center gap-1 text-red-600">
        <XCircleIcon size={22} weight="fill" />
        {VACATION_REQUESTS_STRINGS.APPLY_RESULT_ERROR_LABEL}
      </span>
    );

  const messageBody = (rowData: ApplyResultRowT) =>
    rowData.isSuccess ? (
      <span className="text-gray-500">—</span>
    ) : (
      <span className="text-red-700">{rowData.message}</span>
    );

  const footer = (
    <div className="flex justify-end">
      <Button
        label={VACATION_REQUESTS_STRINGS.APPLY_RESULT_CLOSE_LABEL}
        icon="pi pi-times"
        onClick={onClose}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={`${VACATION_REQUESTS_STRINGS.APPLY_RESULT_TITLE} — ${header}`}
      onHide={onClose}
      style={{ width: "48rem" }}
      footer={footer}
      modal
      dismissableMask={false}
      closeOnEscape
    >
      <DataTable value={data} responsiveLayout="scroll" stripedRows>
        <Column
          field="fullName"
          header={VACATION_REQUESTS_STRINGS.APPLY_RESULT_COLUMN_COLLABORATOR}
        />
        <Column
          field="requestType"
          header={VACATION_REQUESTS_STRINGS.APPLY_RESULT_COLUMN_TYPE}
        />
        <Column
          header={VACATION_REQUESTS_STRINGS.APPLY_RESULT_COLUMN_STATUS}
          body={statusBody}
          align="center"
          style={{ width: "10rem" }}
        />
        <Column
          header={VACATION_REQUESTS_STRINGS.APPLY_RESULT_COLUMN_MESSAGE}
          body={messageBody}
        />
      </DataTable>
    </Dialog>
  );
};

export default ApplyResultDialog;
